import { baseApi } from './baseApi';
import { Transaction } from 'models/Transaction';
import { QueryParams, TAG_TYPES } from 'types';
import { getQueryParams } from 'utils';

const URL_TRANSACTION = 'transactions';

const transactionApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTransactions: builder.query<
      {
        data: {
          rows: Transaction[];
          count: number;
          maxTransactionAmount: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_TRANSACTION, method: 'GET' };
        return {
          url: `${URL_TRANSACTION}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Transaction, id })),
              { type: TAG_TYPES.Transaction, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Transaction, id: 'LIST' }];
      },
    }),

    getTransactionById: builder.query<
      {
        data: Transaction;
        success: boolean;
      },
      {
        id: string;
        query?: QueryParams;
      }
    >({
      query: (params) => {
        const queryString = params.query ? `?${getQueryParams(params.query)}` : '';
        return {
          url: `${URL_TRANSACTION}/${params.id}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, params) => [{ type: TAG_TYPES.Transaction, id: params.id }],
    }),

    getTransactionsByRefId: builder.query<
      {
        data: {
          rows: Transaction[];
          count: number;
        };
        success: boolean;
      },
      {
        refId: string;
        query?: QueryParams;
      }
    >({
      query: (params) => {
        const mergedQuery = {
          ...(params.query || {}),
          ...{
            filter: `refId:eq:${params.refId}`,
            include: 'transactionMethod',
          },
        } as QueryParams;
        const queryString = getQueryParams(mergedQuery);
        return {
          url: `${URL_TRANSACTION}?${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, params) => [{ type: TAG_TYPES.Transaction, id: `${params.refId}` }],
    }),

    createTransaction: builder.mutation<Transaction, Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>>(
      {
        query: (data) => ({
          url: URL_TRANSACTION,
          method: 'POST',
          data,
        }),
        invalidatesTags: (result, error, data) => [
          { type: TAG_TYPES.Transaction, id: 'LIST' },
          { type: TAG_TYPES.Transaction, id: `${data.refId}` },
        ],
      },
    ),

    updateTransaction: builder.mutation<Transaction, Partial<Transaction> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${URL_TRANSACTION}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Transaction, id },
        { type: TAG_TYPES.Transaction, id: 'LIST' },
        { type: TAG_TYPES.Transaction, id: result?.refId || 'LIST' },
      ],
    }),

    deleteTransaction: builder.mutation<void, string>({
      query: (id) => ({
        url: `${URL_TRANSACTION}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPES.Transaction, id },
        { type: TAG_TYPES.Transaction, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useGetTransactionByIdQuery,
  useGetTransactionsByRefIdQuery,
  useLazyGetTransactionsByRefIdQuery,
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
  useDeleteTransactionMutation,
} = transactionApiService;
